import moment from 'moment';
import { okrDetailConstants } from '../constants/okr/okrDetails.constants';

const {
  EX_DATE_ACTIVE_ON,
  EX_DATE_EXACT_MATCH,
  EX_DATE_SPAN_PERIOD,
} = okrDetailConstants;

export const TIME_PERIOD_OPERATOR = {
  FALLS_WITHIN: 'falls within',
  EXACTLY_EQUALS: 'exactly equals',
  ACTIVE_ON: 'active on',
};

export const TIME_PERIOD_SELECT_OPERATORS = [
  TIME_PERIOD_OPERATOR.EXACTLY_EQUALS,
  TIME_PERIOD_OPERATOR.FALLS_WITHIN,
  TIME_PERIOD_OPERATOR.ACTIVE_ON,
];

const OPERATOR_TO_EX_DATE = {
  [TIME_PERIOD_OPERATOR.ACTIVE_ON]: EX_DATE_ACTIVE_ON,
  [TIME_PERIOD_OPERATOR.EXACTLY_EQUALS]: EX_DATE_EXACT_MATCH,
  [TIME_PERIOD_OPERATOR.FALLS_WITHIN]: EX_DATE_SPAN_PERIOD,
};

const EX_DATE_TO_OPERATOR = Object.fromEntries(
  Object.entries(OPERATOR_TO_EX_DATE).map(([operator, exDate]) => [String(exDate), operator]),
);

export const mapExDateToTimePeriodOperator = (exDate) => (
  EX_DATE_TO_OPERATOR[String(exDate)] ?? TIME_PERIOD_OPERATOR.FALLS_WITHIN
);

export const mapTimePeriodOperatorToExDate = (operator) => (
  OPERATOR_TO_EX_DATE[operator] ?? EX_DATE_SPAN_PERIOD
);

const VALID_EX_DATE_VALUES = new Set(Object.values(OPERATOR_TO_EX_DATE));

export const parseExDateValue = (exDate, fallback = EX_DATE_SPAN_PERIOD) => {
  const parsed = Number(exDate);
  return VALID_EX_DATE_VALUES.has(parsed) ? parsed : fallback;
};

const parseTimePeriodDate = (date) => {
  if (!date) {
    return '';
  }

  const parsed = moment(date, ['MM/DD/YYYY', 'M/D/YYYY', 'YYYY-MM-DD'], true);

  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

export const isTimePeriodFilterApplied = ({
  startDate, endDate,
}) => Boolean(startDate || endDate);

export const mapTimePeriodCriterionToParams = (filterValues) => {
  const timePeriod = filterValues?.group?.criteria?.find((c) => c.fieldName === 'timePeriod');

  if (!timePeriod) {
    return { startDate: '', endDate: '', exDate: EX_DATE_SPAN_PERIOD };
  }

  if (!timePeriod.values?.length) {
    return { startDate: '', endDate: '', exDate: EX_DATE_SPAN_PERIOD };
  }

  const { startDate, endDate } = timePeriod.values[0] || {};

  return {
    exDate: mapTimePeriodOperatorToExDate(timePeriod.operator),
    startDate: startDate ? moment(startDate).format('MM/DD/YYYY') : '',
    endDate: endDate ? moment(endDate).format('MM/DD/YYYY') : '',
  };
};

export const buildTimePeriodInitialCriterion = ({ startDate, endDate, exDate }) => {
  const operator = mapExDateToTimePeriodOperator(exDate);
  const parsedStartDate = parseTimePeriodDate(startDate);
  const parsedEndDate = parseTimePeriodDate(endDate);

  return {
    fieldName: 'timePeriod',
    fieldType: 'time-periods-select',
    operator,
    values: parsedStartDate && parsedEndDate
      ? [{ startDate: parsedStartDate, endDate: parsedEndDate, label: '' }]
      : [],
    nonRemovable: true,
  };
};

export const getTimePeriodFieldDef = (t, fieldOptions = {}) => ({
  label: t('Time Period'),
  type: 'time-periods-select',
  operators: TIME_PERIOD_SELECT_OPERATORS,
  fieldOptions: {
    halfYearTimePeriods: true,
    multiTimePeriods: false,
    enableAutoDefineUnknownTimePeriod: true,
    ...fieldOptions,
  },
});
